import { getString, fetchAndActivate } from 'firebase/remote-config';
import { remoteConfig } from './firebase';

// GTM instance state
let isInitialized = false;
let gtmId: string | null = null;

/**
 * Initialize Google Tag Manager with Container ID from Remote Config
 */
export const initializeGTM = async (): Promise<void> => {
  if (isInitialized) {
    console.log('[GTM] Already initialized');
    return;
  }

  try {
    // Ensure Remote Config is fetched and activated
    console.log('[GTM] Fetching Remote Config...');
    await fetchAndActivate(remoteConfig);
    console.log('[GTM] Remote Config fetched');

    // Get GTM Container ID from Remote Config
    gtmId = getString(remoteConfig, 'gtm_container_id');

    if (!gtmId) {
      console.warn('[GTM] Container ID not found in Remote Config');
      console.warn('[GTM] Please add "gtm_container_id" (format: GTM-XXXXXXX) to Firebase Remote Config');
      return;
    }

    console.log('[GTM] Container ID found:', gtmId);

    // Initialize GTM dataLayer
    window.dataLayer = window.dataLayer || [];

    // Push GTM initialization
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    // Load GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(script);

    // Add noscript iframe for users without JavaScript
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    isInitialized = true;
    console.log('[GTM] ✅ Google Tag Manager initialized successfully');
  } catch (error) {
    console.error('[GTM] ❌ Failed to initialize Google Tag Manager:', error);
  }
};

/**
 * Check if GTM is initialized
 */
export const isGTMInitialized = (): boolean => {
  return isInitialized;
};

/**
 * Get the GTM Container ID
 */
export const getGTMId = (): string | null => {
  return gtmId;
};

/**
 * Push data to GTM dataLayer
 */
export const pushToDataLayer = (data: Record<string, any>): void => {
  if (!isInitialized) {
    // Silently skip if not initialized yet (initialization is async)
    // This prevents console noise during app startup
    return;
  }

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);

    console.log('[GTM] ✅ Data pushed to dataLayer');
    console.log('[GTM] Data:', JSON.stringify(data, null, 2));
  } catch (error) {
    console.error('[GTM] ❌ Error pushing to dataLayer:', error);
  }
};

// ==================== GTM Event Tracking ====================

/**
 * Track a generic event to GTM
 */
export const trackGTMEvent = (
  eventName: string,
  eventParams?: Record<string, any>
): void => {
  pushToDataLayer({
    event: eventName,
    ...eventParams,
  });
};

/**
 * Track page view
 */
export const trackPageView = (
  pagePath: string,
  pageTitle?: string
): void => {
  pushToDataLayer({
    event: 'page_view',
    page_path: pagePath,
    page_title: pageTitle || document.title,
    page_location: window.location.href,
  });
};

/**
 * Track user login
 */
export const trackGTMUserLogin = (
  method: string,
  userId?: string
): void => {
  pushToDataLayer({
    event: 'login',
    method: method,
    user_id: userId,
  });
};

/**
 * Track user sign up
 */
export const trackGTMSignUp = (
  method: string,
  userId?: string
): void => {
  pushToDataLayer({
    event: 'sign_up',
    method: method,
    user_id: userId,
  });
};

/**
 * Track purchase/subscription
 */
export const trackGTMPurchase = (params: {
  transactionId: string;
  value: number;
  currency: string;
  items?: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity?: number;
  }>;
}): void => {
  pushToDataLayer({
    event: 'purchase',
    transaction_id: params.transactionId,
    value: params.value,
    currency: params.currency,
    items: params.items || [],
  });
};

/**
 * Track subscription cancellation
 */
export const trackGTMCancellation = (params: {
  subscriptionId: string;
  planName: string;
  reason?: string;
}): void => {
  pushToDataLayer({
    event: 'subscription_cancelled',
    subscription_id: params.subscriptionId,
    plan_name: params.planName,
    cancellation_reason: params.reason,
  });
};

/**
 * Set user properties in dataLayer
 */
export const setGTMUserProperties = (properties: Record<string, any>): void => {
  pushToDataLayer({
    event: 'user_properties_set',
    user_properties: properties,
  });
};

// TypeScript declaration for dataLayer
declare global {
  interface Window {
    dataLayer: Array<Record<string, any>>;
  }
}
